// @flow

import React, { useState } from 'react';
import { get, partial } from 'lodash';

import { Screen } from '@zapier/theme';
import {
  type ApiKeys,
  type CreditCardBrandCode,
  type PaymentDetailsCard,
  type PaymentSource,
  type PaymentSourceMethod,
  type SupportedPaymentMethod,
  paymentSourceMethods,
} from '@zapier/payment/src/types';
import { Button, Media, Text } from '@zapier/common-components';
import {
  CreditCardForm,
  PaymentInfo,
  PaymentMethodImageStrip,
} from '@zapier/payment';

import PaymentMethod from '../PaymentMethod';
import PaymentMethodSelect from '../PaymentMethodSelect';

type Props = {
  /** Payment processor API keys */
  apiKeys: ApiKeys,
  /** Currently select method */
  currentMethod?: PaymentSourceMethod,
  /** Error message to show */
  error?: string,
  /** Do not allow user to cancel card edit */
  forceCardUpdate: boolean,
  /** Hide switch component button */
  hideSwitch: boolean,
  /** Show editor */
  isEditing: boolean,
  /** Show loading */
  isEditingInFlight: boolean,
  /** Call to cancel edit */
  onCancelEdit: () => void,
  /** Call to start edit */
  onEdit: () => void,
  /** Call when editor throws an error */
  onErrorEdit: (error: Error) => void,
  /** Call to save current edit */
  onSaveEdit: () => void,
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceMethod) => void,
  /** User's currently saved card details */
  savedSource: PaymentSource,
  /** Shows the new select dropdown UI */
  showSelect: boolean,
  /** Supported payment methods (bank, card etc.) */
  supportedMethods: Array<SupportedPaymentMethod>,
};

const renderLabel = (props: Props, selectedMethod: CreditCardBrandCode) =>
  props.savedSource && !props.isEditing ? (
    <PaymentInfo paymentSource={props.savedSource} />
  ) : (
    <Media queries={{ small: Screen.queries.phoneOnly }}>
      {({ small }) => (
        <React.Fragment>
          {small && !props.hideSwitch ? null : (
            <PaymentMethodImageStrip
              isSmall={true}
              methods={[paymentSourceMethods.card]}
              selectedMethod={props.isEditing ? selectedMethod : null}
            />
          )}

          <h3>
            <Text type="section-head">
              {props.savedSource ? 'Edit Card' : 'Add Card'}
            </Text>
          </h3>
        </React.Fragment>
      )}
    </Media>
  );

const renderAside = (props: Props) => (
  <Media queries={{ small: Screen.queries.phoneOnly }}>
    {({ small }) => {
      if (!props.isEditing) {
        return (
          <Button color="secondary" onClick={props.onEdit}>
            {props.savedSource
              ? `Edit${!small ? ' Payment Method' : ''}`
              : 'Add'}
          </Button>
        );
      } else {
        const switchUI = props.showSelect ? (
          <PaymentMethodSelect
            currentMethod={props.currentMethod}
            onChange={props.onSelectSource}
            supportedMethods={props.supportedMethods}
          />
        ) : (
          <Button
            backgroundType="clear"
            color="primary"
            isDisabled={props.isEditingInFlight}
            label="Switch to PayPal"
            onClick={partial(
              props.onSelectSource,
              paymentSourceMethods.paypal,
              true
            )}
          >
            Switch to PayPal
          </Button>
        );
        return props.hideSwitch ? null : switchUI;
      }
    }}
  </Media>
);

const PaymentMethodCard = (props: Props) => {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const cardDetails: PaymentDetailsCard = get(props, [
    'savedSource',
    'details',
  ]);
  const cardMethod = props.supportedMethods.find(
    m => m.method === paymentSourceMethods.card
  );

  return (
    <PaymentMethod
      isEditing={props.isEditing}
      renderAside={partial(renderAside, props)}
      renderLabel={partial(renderLabel, props, selectedMethod)}
    >
      <CreditCardForm
        apiKeys={props.apiKeys}
        error={props.error}
        hasCancelButton={!props.forceCardUpdate}
        hasPaymentSource={Boolean(props.savedSource)}
        isInFlight={props.isEditingInFlight}
        onCancel={props.onCancelEdit}
        onCardNumberChange={setSelectedMethod}
        onError={props.onErrorEdit}
        onSave={props.onSaveEdit}
        paymentProcessor={get(cardMethod, ['paymentProcessor'])}
        savedCard={cardDetails}
      />
    </PaymentMethod>
  );
};

export default PaymentMethodCard;
